import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { callPostApi } from "../../function/ApiCallFunction";
import { adminPost } from "./adminApi";
import { clubPost } from "../club/clubApi";
import { consoleColors, gridSx, primaryButtonSx } from "./consoleTheme";
import IndiaDataGrid from "../../include/IndiaDataGrid";

const KINDS = [
  { id: "", label: "All transactions" },
  { id: "sale", label: "Sales" },
  { id: "wallet", label: "Wallet" },
  { id: "coupon", label: "Coupons" },
  { id: "winner", label: "Winners" },
  { id: "store", label: "Stores" },
  { id: "print", label: "Print" },
];

const columns = [
  { field: "date_added", headerName: "Date", width: 160 },
  { field: "action", headerName: "Action", width: 170 },
  { field: "store_name", headerName: "Store / club", flex: 1, minWidth: 140 },
  { field: "customer_name", headerName: "Customer", width: 150 },
  { field: "customer_mobile", headerName: "Mobile", width: 130 },
  { field: "amount", headerName: "Amount", width: 110 },
  { field: "reference", headerName: "Reference", width: 140 },
  { field: "comment", headerName: "Details", flex: 1.4, minWidth: 180 },
];

export default function AuditTrail({ source = "admin" }) {
  const { loginData, clubData } = useContext(DataContext);
  const sessionStoreId = loginData?.store_info?.store_id || "";
  const clubId = clubData?.club_info?.club_id || "";
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(source === "store" ? sessionStoreId : "");
  const [kind, setKind] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchKey, setSearchKey] = useState("");

  useEffect(() => {
    if (source !== "admin") return;
    adminPost("ADMIN_STORE_LIST").then((result) => {
      if (result.status === "200") setStores(result.data || []);
    });
  }, [source]);

  const load = useCallback(async () => {
    const payload = { kind, startDate, endDate, searchKey, start: 0, limit: 300 };
    let result;
    if (source === "store") {
      if (!sessionStoreId) return;
      result = await callPostApi("store-draw", { mod: "AUDIT_LIST", data_arr: { ...payload, store_id: sessionStoreId } });
    } else if (source === "club") {
      if (!clubId) return;
      result = await clubPost("AUDIT_LIST", { ...payload, club_id: clubId });
    } else {
      result = await adminPost("AUDIT_LIST", { ...payload, store_id: storeId });
    }
    if (result.status === "200" && Array.isArray(result.data)) setRows(result.data);
    else toast.error(result?.status_message || "Could not load audit trail");
  }, [source, sessionStoreId, clubId, storeId, kind, startDate, endDate, searchKey]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center", background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 1.5 }}>
        {source === "admin" && (
          <TextField select size="small" label="Store" value={storeId} onChange={(e) => setStoreId(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="">All stores</MenuItem>
            {stores.map((store) => <MenuItem key={store.store_id} value={store.store_id}>{store.store_name}</MenuItem>)}
          </TextField>
        )}
        <TextField select size="small" label="Type" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 170 }}>
          {KINDS.map((item) => <MenuItem key={item.id || "all"} value={item.id}>{item.label}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Search" value={searchKey} onChange={(e) => setSearchKey(e.target.value)} />
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <Button variant="contained" onClick={load} sx={primaryButtonSx}>Apply</Button>
      </Box>
      <Box sx={{ height: 640, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
        <IndiaDataGrid
          rows={rows.map((row) => ({ ...row, id: row.audit_id }))}
          columns={columns}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          sx={gridSx}
        />
      </Box>
    </Box>
  );
}
